import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationAlertsContractTrafficType } from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import { useTracking } from "@similarweb/tracking-provider";
import { Badge } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import type { ICardLayoutProps } from "../../common/card-layout/CardLayout";
import { CardLayout } from "../../common/card-layout/CardLayout";
import type { ExpandKeySuffix } from "../../common/card-layout/ExpandableText";
import { ExpandableText } from "../../common/card-layout/ExpandableText";
import { Link } from "../../common/card-layout/Link";

import type { ICommonNavigationParams } from "./useCardsNavigation";
import { useLandingPagesNavigation } from "./useCardsNavigation";

interface ILandingPageAlertProps extends Pick<ICardLayoutProps, "iconUrl">, ICommonNavigationParams {
    date: string;
    domain: string;
    landingPages: string[];
    text: ReactNode;
    title: ReactNode;
    trafficType: ActivationAlertsContractTrafficType;
}

export const LandingPageAlert = ({
    country,
    date,
    domain,
    iconUrl,
    landingPages,
    text,
    title,
    trafficType,
}: ILandingPageAlertProps) => {
    const { track } = useTracking();
    const { translate } = useTranslate();
    const { seeMoreLandingPagesLink, navigateToLandingPage } = useLandingPagesNavigation({
        domain,
        country,
        trafficType,
    });
    const handleExpand = (keySuffix: ExpandKeySuffix) => {
        track("Alert Card", "click", `${keySuffix} alert/alerts/landing-page`);
    };

    return (
        <CardLayout
            iconUrl={iconUrl}
            onClick={navigateToLandingPage}
            data-automation="landing-page-alert"
            data-domain={domain}
        >
            <CardLayout.Header>
                <Badge data-automation="alert-badge">{translate("activation.alerts.cards.landing-page.badge")}</Badge>
                <CardLayout.Title>{title}</CardLayout.Title>
                <Text
                    flexShrink={0}
                    fontVariant="subtitle1"
                    color={colorsPalettes.carbon[250]}
                >
                    {date}
                </Text>
                <CardLayout.Arrow
                    tooltip={translate("activation.alerts.cards.landing-page.arrow.tooltip")}
                    data-automation="alert-see-more"
                />
            </CardLayout.Header>
            <CardLayout.Content>
                <ExpandableText onTrack={handleExpand}>{text}</ExpandableText>
            </CardLayout.Content>
            <CardLayout.Footer label={translate("activation.alerts.cards.landing-page.footer.label")}>
                {landingPages.map((landingPage) => (
                    <Link
                        key={landingPage}
                        href={`https://${domain}${landingPage}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        direction="rtl"
                        truncate
                        onClick={() => {
                            track("Alert Card", "click", "open landing-page/alerts/landing-page");
                        }}
                    >
                        {landingPage}
                    </Link>
                ))}
                <Link
                    href={seeMoreLandingPagesLink}
                    flexShrink={0}
                    onClick={() => {
                        track("Alert Card", "click", "see more landing-pages/alerts/landing-page");
                    }}
                    data-automation="see-more-link"
                >
                    {translate("activation.alerts.cards.common.footer.link")}
                </Link>
            </CardLayout.Footer>
        </CardLayout>
    );
};
